import { useCallback } from 'react';

import { useLazyGetDialpadCallArtifactQuery } from '@/services/api/dialpadService';
import { useLazyGetRCCallArtifactQuery } from '@/services/api/ringcentralService';

import type { TelephonyProvider } from './useTelephonyProvider';

export interface NormalizedArtifact {
  call_id: string;
  conversation_id?: string | null;
  user_name?: string | null;
  artifact_id?: string | null;
  matter_id?: string | null;
}

export const useCallArtifact = () => {
  const [resolveDialpad] = useLazyGetDialpadCallArtifactQuery();
  const [resolveRC] = useLazyGetRCCallArtifactQuery();

  const resolveArtifact = useCallback(
    async (
      callId: string,
      provider: TelephonyProvider,
      fromNumber?: string,
    ): Promise<NormalizedArtifact | null> => {
      try {
        if (provider === 'ringcentral') {
          const { data } = await resolveRC({
            callId,
            fromNumber: fromNumber || undefined,
          });
          if (!data) return null;
          return {
            call_id: data.call_id,
            conversation_id: data.conversation_id,
            user_name: data.ringcentral_user_name,
            artifact_id: data.artifact_id,
            matter_id: data.matter_id,
          };
        }

        const { data } = await resolveDialpad({
          callId,
          fromNumber: fromNumber || undefined,
        });
        if (!data) return null;
        return {
          call_id: data.call_id,
          conversation_id: data.conversation_id,
          user_name: data.dialpad_user_name,
          artifact_id: data.artifact_id,
          matter_id: data.matter_id,
        };
      } catch {
        return null;
      }
    },
    [resolveDialpad, resolveRC],
  );

  return { resolveArtifact };
};
